import { type RefObject, useLayoutEffect } from "react";

// Align the stand-in action's right edge with the furthest-right [data-col] element — in the base
// setup view the Saknar rail (or the board's rightmost column once the rail has wrapped below); in
// the boss view the matrix table when it fits, clamped to the screen edge once it overflows. That
// edge depends on how the columns/rail/matrix have reflowed (not a fixed width), so it's measured
// rather than expressed in CSS. Only while the action shares the select's line (≥485px); below that
// it's a full-width button on its own row. `reflowKey` re-runs the effect (re-queries and re-observes
// the [data-col] set) when the view swaps the content out from under it.
export function useAlignActionToContentEdge(
  actionRef: RefObject<HTMLDivElement | null>,
  { reflowKey }: { reflowKey: string },
) {
  // biome-ignore lint/correctness/useExhaustiveDependencies: reflowKey isn't read here — it re-runs the effect to re-query/re-observe [data-col] after the view swaps the DOM.
  useLayoutEffect(() => {
    const action = actionRef.current;
    const row = action?.parentElement;
    if (!action || !row) return;
    action.style.marginRight = "";
    const wide = window.matchMedia("(min-width: 485px)");
    const align = () => {
      if (!wide.matches) {
        action.style.marginRight = "";
        return;
      }
      const cols = document.querySelectorAll<HTMLElement>("[data-col]");
      if (!cols.length) {
        action.style.marginRight = "";
        return;
      }
      const targetRight = Math.max(
        ...Array.from(cols, (c) => c.getBoundingClientRect().right),
      );
      const rowRight = row.getBoundingClientRect().right;
      action.style.marginRight = `${Math.max(0, Math.round(rowRight - targetRight))}px`;
    };
    align();
    const observer = new ResizeObserver(align);
    observer.observe(row);
    const board = document.querySelector("[data-setup-board]");
    if (board) observer.observe(board);
    for (const col of document.querySelectorAll<HTMLElement>("[data-col]")) {
      observer.observe(col);
    }
    wide.addEventListener("change", align);
    return () => {
      observer.disconnect();
      wide.removeEventListener("change", align);
    };
    // Re-alignment is driven by the ResizeObserver (row + measured columns), not renders.
  }, [reflowKey, actionRef]);
}
